import { VoucherResponse } from '@/app/types/voucher'
import { Party } from '@/app/types/party'
import { generateThermalReceipt } from '@/app/components/ThermalReceipt'
import { thermalReceiptToHtml } from '@/app/utils/thermalReceiptToHtml'
import { format } from 'date-fns'
import { PartyClosingBalanceResponse } from '@/app/types/reports'

export interface ThermalPrinterConfig {
  paperSize: '80mm' | '58mm' | '44mm'
  printerType: 'epson' | 'star' | 'tsc' | 'generic'
  characterSet?: 'pc437' | 'pc850' | 'pc860' | 'pc863' | 'pc865' | 'windows1252'
  interface?: 'usb' | 'serial' | 'tcp' | 'bluetooth'
  useEscPos?: boolean
  useHtmlTemplate?: boolean
  htmlTemplate?: string
  includeLogo?: boolean
  includeBarcode?: boolean
  includeQrCode?: boolean
  charactersPerLine?: number
}

export interface CompanyDetails {
  name: string
  address: string
  cityStatePin: string
  phone?: string
  gstin?: string
  email?: string
  website?: string
}

export class ThermalPrinterService {
  private config: ThermalPrinterConfig

  constructor(config: ThermalPrinterConfig) {
    this.config = config
  }

  async generateInvoiceReceipt(
    invoice: VoucherResponse,
    party: Party | undefined,
    companyDetails: CompanyDetails,
    closingBalance?: PartyClosingBalanceResponse
  ): Promise<Uint8Array> {
    try {
      // Use react-thermal-printer to generate receipt
      const printerType = (this.config.printerType === 'generic' || this.config.printerType === 'tsc') ? 'epson' : this.config.printerType as 'epson' | 'star'
      return await generateThermalReceipt(
        invoice,
        party,
        companyDetails,
        this.config.paperSize,
        printerType,
        closingBalance
      )
    } catch (error) {
      console.error('Error generating thermal receipt:', error)
      throw new Error('Failed to generate thermal receipt')
    }
  }

  // Generate HTML-formatted receipt for fallback/preview
  generateTextReceipt(
    invoice: VoucherResponse,
    party: Party | undefined,
    companyDetails: CompanyDetails,
    closingBalance?: PartyClosingBalanceResponse
  ): string {
    // Use the HTML formatter for proper formatting
    return thermalReceiptToHtml(invoice, party, companyDetails, this.config.paperSize, this.config.charactersPerLine, closingBalance)
  }

  // Check if browser supports direct USB printing
  async checkUSBPrinterSupport(): Promise<boolean> {
    if ('usb' in navigator) {
      try {
        await (navigator as any).usb.getDevices()
        return true
      } catch (error) {
        console.log('USB access not available:', error)
        return false
      }
    }
    return false
  }

  // Print using Web USB API if available
  async printViaUSB(data: Uint8Array): Promise<boolean> {
    if (!await this.checkUSBPrinterSupport()) {
      return false
    }

    try {
      const device = await (navigator as any).usb.requestDevice({
        filters: [
          { vendorId: 0x04b8 }, // Epson
          { vendorId: 0x0519 }, // Star Micronics
          { vendorId: 0x0dd4 }, // Custom
          { vendorId: 0x0fe6 }, // Sewoo
        ]
      })

      if (!device) {
        throw new Error('No USB printer selected')
      }

      await device.open()
      await device.selectConfiguration(1)
      await device.claimInterface(0)

      // Send data to printer
      await device.transferOut(1, data)

      await device.close()
      return true
    } catch (error) {
      console.error('USB printing failed:', error)
      return false
    }
  }

  // Print using browser's window.print() as fallback
  printViaWindowPrint(receiptHTML: string, paperSize: string): void {
    const printWindow = window.open('', '_blank', 'width=300,height=600')
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Receipt</title>
          <style>
            @page {
              size: ${paperSize} auto;
              margin: 0;
            }
            @media print {
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                font-family: 'Courier New', Courier, monospace !important;
                font-size: 11px !important;
                line-height: 1.3 !important;
                color: black !important;
                background: white !important;
              }
              .receipt-content {
                margin: 0 !important;
                padding: 5px 3px !important;
                white-space: pre !important;
              }
              strong {
                font-weight: bold !important;
              }
            }
            body {
              margin: 0;
              padding: 0;
              font-family: 'Courier New', Courier, monospace;
              font-size: 11px;
              line-height: 1.3;
            }
            .receipt-content {
              margin: 0;
              padding: 5px 3px;
              white-space: pre;
              font-family: inherit;
            }
            strong {
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="receipt-content">${receiptHTML}</div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => window.close(), 500);
            }
          </script>
        </body>
        </html>
      `)
      printWindow.document.close()
    }
  }
}